import React from "react";

import ace from "ace-builds";
import "ace-builds/src-noconflict/mode-json";

import { Indent, getIndentString, Editor } from "@tableplus/toolkit";
import { useEditor, EditorIndent, Border } from "@tableplus/toolkit";
import { Toolbar } from "./toolbar/toolbar";
import s from "./edit.module.scss";
import { DivPx, background } from "@moai/core";
import { Prefs } from "prefs/prefs";

// https://github.com/ajaxorg/ace/issues/3913
// eslint-disable-next-line
const workerUrl = require("file-loader!ace-builds/src-noconflict/worker-json.js");
ace.config.setModuleUrl("ace/mode/json_worker", workerUrl);

interface Props {
    prefs: Prefs;
    value: string;
    setValue: React.Dispatch<React.SetStateAction<string>>;
}

const jsonFormat = (value: string, indent?: Indent): string | null => {
    let obj = {};
    try {
        obj = JSON.parse(value);
    } catch (e) {
        console.log(e); // @TODO: should we tell?
        return null;
    }
    const space = indent ? getIndentString(indent) : undefined;
    return JSON.stringify(obj, undefined, space);
};

const removeCommands = [
    "addLineAfter", // ctrl-enter ---> conflict with "format"
    "expandtoline", // ctrl-l ---> browser's default
];

const getEditorIndent = (indent: Indent): EditorIndent => ({
    tab: indent.kind === "tab",
    width: indent.width ?? indent.custom,
});

export const Edit: React.FC<Props> = ({ prefs, value, setValue }) => {
    const config = { value, mode: "ace/mode/json" };
    const editor = useEditor({ config, removeCommands });

    const { onChange } = editor;
    React.useEffect(() => {
        return onChange((params) => setValue(params.value));
    }, [onChange, setValue]);

    const { setIndent, getValue, setValue: editorSetValue } = editor;
    const { indent } = prefs;
    const format = React.useCallback(() => {
        const value = jsonFormat(getValue() ?? "", indent);
        if (value !== null) {
            editorSetValue(value);
            setIndent(getEditorIndent(indent));
        }
    }, [getValue, indent, setIndent, editorSetValue]);

    const { addCommand } = editor;
    React.useEffect(() => {
        const key = { mac: "cmd-enter", win: "ctrl-enter" };
        return addCommand({ key, name: "format", fn: format });
    }, [addCommand, format]);

    const uglify = React.useCallback(() => {
        const value = jsonFormat(getValue() ?? "");
        if (value !== null) editorSetValue(value);
    }, [getValue, editorSetValue]);

    const toolbar = (
        <Toolbar
            uglify={uglify}
            format={format}
            prefs={prefs}
            getValue={getValue}
        />
    );
    return (
        <div className={`${s.container} ${background.secondary}`}>
            <DivPx size={16} />
            <div className={s.toolbar}>{toolbar}</div>
            <DivPx size={16} /> <Border color="strong" /> <DivPx size={16} />
            <div className={s.editor}>
                <Editor editor={editor} />
            </div>
        </div>
    );
};
